import { Photo, Brand } from "../types/photos";
import { supabase } from '@/lib/supabase';

const STORAGE_KEY = "uploaded_photos";
const BRANDS_STORAGE_KEY = "registered_brands";

// Generate a unique slug for brand upload pages
export const generateUniqueSlug = (name: string, id: string): string => {
  const nameSlug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .replace(/\s+/g, '');

  return `${nameSlug}-${id}`;
};

// Brand authentication functions
export const registerBrand = async (brand: Brand): Promise<Brand> => {
  try {
    // First, create the auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: brand.email,
      password: brand.password,
      options: {
        data: {
          name: brand.name
        }
      }
    });

    if (authError) {
      throw new Error(authError.message);
    }

    if (!authData.user) {
      throw new Error("Failed to create user account");
    }

    // Generate the unique upload slug with the new ID
    const uniqueUploadSlug = generateUniqueSlug(brand.name, authData.user.id);

    // Then, create the brand record in the database
    const { error: dbError } = await supabase
      .from('brands')
      .insert({
        id: authData.user.id,
        name: brand.name,
        email: brand.email,
        password: brand.password,
        unique_upload_slug: uniqueUploadSlug,
        ugc_discount_amount: 25, // Default discount amount
        setup_completed: false // Set to false for new brand registrations
      });

    if (dbError) {
      // If database insert fails, we should clean up the auth user
      await supabase.auth.signOut();
      throw new Error(dbError.message);
    }

    // Return the complete brand object with the correct slug format
    return {
      ...brand,
      id: authData.user.id,
      uniqueUploadSlug,
      storeCredit: 0, // For backward compatibility
      createdAt: new Date().toISOString()
    };
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

// Update a brand's unique upload slug
export const updateBrandSlug = async (brand: Brand): Promise<void> => {
  try {
    const { error } = await supabase
      .from('brands')
      .update({ unique_upload_slug: brand.uniqueUploadSlug })
      .eq('id', brand.id);

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error("Error updating brand slug:", error);
    throw error;
  }
};

export const loginBrand = async (email: string, password: string): Promise<Brand> => {
  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (authError) {
    throw new Error("Invalid credentials");
  }

  if (!authData.user) {
    throw new Error("No user found");
  }

  const { data: brandData, error: dbError } = await supabase
    .from('brands')
    .select('*')
    .eq('id', authData.user.id)
    .single();

  if (dbError || !brandData) {
    throw new Error("Failed to fetch brand data");
  }

  const brand = {
    id: brandData.id,
    name: brandData.name,
    email: brandData.email,
    password: brandData.password,
    uniqueUploadSlug: brandData.unique_upload_slug,
    storeCredit: brandData.store_credit,
    createdAt: brandData.created_at
  };

  return brand;
};

export const logoutBrand = async (): Promise<void> => {
  await supabase.auth.signOut();
};

export const getCurrentBrand = async (): Promise<Brand | null> => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();

  if (authError || !user) {
    return null;
  }

  const { data: brandData, error: dbError } = await supabase
    .from('brands')
    .select('*')
    .eq('id', user.id)
    .single();

  if (dbError || !brandData) {
    return null;
  }

  return {
    id: brandData.id,
    name: brandData.name,
    email: brandData.email,
    password: brandData.password,
    uniqueUploadSlug: brandData.unique_upload_slug,
    storeCredit: brandData.store_credit,
    createdAt: brandData.created_at
  };
};

export const getBrands = async (): Promise<Brand[]> => {
  const { data: brands, error } = await supabase
    .from('brands')
    .select('*');

  if (error) {
    console.error("Error fetching brands:", error);
    return [];
  }

  return brands.map(brandData => ({
    id: brandData.id,
    name: brandData.name,
    email: brandData.email,
    password: brandData.password,
    uniqueUploadSlug: brandData.unique_upload_slug,
    storeCredit: brandData.store_credit,
    createdAt: brandData.created_at
  }));
};

export const getBrandBySlug = async (slug: string): Promise<Brand | null> => {
  try {
    // First try to get from cache
    const brands = getCachedData<Brand[]>(BRANDS_STORAGE_KEY) || [];
    const cachedBrand = brands.find(b => b.uniqueUploadSlug === slug);
    if (cachedBrand) {
      return cachedBrand;
    }

    // If not in cache, fetch from database
    const { data: brandData, error } = await supabase
      .from('brands')
      .select('id, name, email, unique_upload_slug, store_credit, created_at')
      .eq('unique_upload_slug', slug)
      .single();

    if (error) {
      console.error("Error fetching brand:", error);
      return null;
    }

    if (!brandData) {
      return null;
    }

    const brand = {
      id: brandData.id,
      name: brandData.name,
      email: brandData.email,
      uniqueUploadSlug: brandData.unique_upload_slug,
      storeCredit: brandData.store_credit,
      createdAt: brandData.created_at
    };

    // Update cache
    setCachedData(BRANDS_STORAGE_KEY, [...brands, brand]);
    return brand;
  } catch (error) {
    console.error("Error in getBrandBySlug:", error);
    return null;
  }
};

// Photo management functions
export const savePhoto = async (photo: Photo, file: File): Promise<void> => {
  try {
    // First verify the brand exists
    const { data: brandData, error: brandError } = await supabase
      .from('brands')
      .select('id')
      .eq('id', photo.brandId)
      .single();

    if (brandError || !brandData) {
      console.error("Brand not found:", photo.brandId);
      throw new Error("Brand not found");
    }

    // Upload the image to Supabase Storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${photo.brandId}/${fileName}`;

    console.log("Starting photo upload:", {
      brandId: photo.brandId,
      filePath,
      fileName
    });

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('photos')
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    console.log("File uploaded successfully:", uploadData);

    // Get the public URL for the uploaded image
    const { data: { publicUrl } } = supabase.storage
      .from('photos')
      .getPublicUrl(filePath);

    console.log("Got public URL:", publicUrl);

    // Prepare the photo data for database
    const photoData = {
      brand_id: photo.brandId,
      customer_name: photo.customerName,
      customer_email: photo.customerEmail || '',
      image_url: publicUrl,
      product_name: photo.productName,
      status: photo.status,
      submission_date: photo.submissionDate
    };

    console.log("Attempting to save photo data:", photoData);

    // Save to database
    const { data: dbData, error: dbError } = await supabase
      .from('photos')
      .insert(photoData)
      .select();

    if (dbError) {
      console.error("Database error:", dbError);
      // If database insert fails, we should clean up the uploaded file
      await supabase.storage
        .from('photos')
        .remove([filePath]);
      throw new Error(`Failed to save photo data: ${dbError.message}`);
    }

    console.log("Photo saved to database successfully:", dbData);

  } catch (error) {
    console.error("Error in savePhoto:", error);
    throw error;
  }
};

export const getPhotos = async (): Promise<Photo[]> => {
  try {
    console.log("Fetching photos from Supabase...");

    // First, let's check if the table exists and has any data
    const { count, error: countError } = await supabase
      .from('photos')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      console.error("Error checking photo count:", countError);
      return [];
    }

    console.log("Total photos in database:", count);

    // Now fetch the actual photos
    const { data: photos, error } = await supabase
      .from('photos')
      .select('id, brand_id, customer_name, customer_email, product_name, image_url, status, submission_date')
      .order('submission_date', { ascending: false });

    if (error) {
      console.error("Error fetching photos:", error);
      return [];
    }

    console.log("Raw photos from database:", photos);

    const transformedPhotos = photos.map(photo => ({
      id: photo.id,
      imageUrl: photo.image_url,
      customerName: photo.customer_name,
      customerEmail: photo.customer_email || '',
      productName: photo.product_name,
      submissionDate: photo.submission_date,
      status: photo.status as "pending" | "approved" | "rejected",
      brandId: photo.brand_id
    }));

    console.log("Transformed photos:", transformedPhotos);
    return transformedPhotos;
  } catch (error) {
    console.error("Error in getPhotos:", error);
    return [];
  }
};

export const getBrandPhotos = async (brandId: string): Promise<Photo[]> => {
  try {
    console.log("Fetching photos for brand:", brandId);

    const { data: photos, error } = await supabase
      .from('photos')
      .select('id, brand_id, customer_name, customer_email, product_name, image_url, status, submission_date')
      .eq('brand_id', brandId)
      .order('submission_date', { ascending: false });

    if (error) {
      console.error("Error fetching brand photos:", error);
      return [];
    }

    console.log("Raw brand photos:", photos);

    return photos.map(photo => ({
      id: photo.id,
      imageUrl: photo.image_url,
      customerName: photo.customer_name,
      customerEmail: photo.customer_email || '',
      productName: photo.product_name,
      submissionDate: photo.submission_date,
      status: photo.status as "pending" | "approved" | "rejected",
      brandId: photo.brand_id
    }));
  } catch (error) {
    console.error("Error in getBrandPhotos:", error);
    return [];
  }
};

export const updatePhotoStatus = async (id: string, status: "pending" | "approved" | "rejected"): Promise<void> => {
  try {
    // Try the admin RPC function first (which bypasses RLS)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('admin_update_photo_status', {
        photo_id: id,
        new_status: status
      });

    if (rpcError) {
      console.error("RPC function call failed, falling back to direct update:", rpcError);

      // Fall back to direct update (will only work if user has permission via RLS)
      const { error } = await supabase
        .from('photos')
        .update({ status })
        .eq('id', id);

      if (error) {
        console.error("Error updating photo status:", error);
        throw error;
      }
    } else {
      console.log("Photo status updated via admin function:", rpcData);
    }
  } catch (error) {
    console.error("Error in updatePhotoStatus:", error);
    throw error;
  }
};

export const updatePhotoDiscountCode = async (id: string, discountCode: string, discountCodeSent: boolean = false): Promise<void> => {
  try {
    // Try the admin RPC function first (which bypasses RLS)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('admin_update_photo_discount', {
        photo_id: id,
        discount_code: discountCode,
        discount_sent: discountCodeSent
      });

    if (rpcError) {
      console.error("RPC function call failed, falling back to direct update:", rpcError);

      // Fall back to direct update (will only work if user has permission via RLS)
      const { error } = await supabase
        .from('photos')
        .update({
          discount_code: discountCode,
          discount_code_sent: discountCodeSent
        })
        .eq('id', id);

      if (error) {
        console.error("Error updating photo discount code:", error);
        throw error;
      }
    } else {
      console.log("Photo discount code updated via admin function:", rpcData);
    }
  } catch (error) {
    console.error("Error in updatePhotoDiscountCode:", error);
    throw error;
  }
};
